import { Pie } from "react-chartjs-2"
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js'
import { BaseTable, Button, DashboardFilter, HeaderMenu, Loading, MultipleChart, Select, TableHeader } from "components"
import { convertNumber } from "utils"
import { useEffect, useState } from "react"
import moment from "moment"
import { ValueStockSection, TotalUnits } from "./components"
import { DashboardFillType } from "types"
import { useCurrentStockPerformence, useEmployeeCostToExpense, useEmployeeCostToRevenue, useGMP, useOverheadRatio, useSohToRevenue, useStockAvailablityValue } from "./utils"
import { LabelListEnum } from "./enum"
import { useProductCategory } from "../Register/utils"
import { useCurrentStockPositionValue } from "./utils/hooks/useCurrentStockPositionValue"
import { fillterDataType } from "./types/CurrentStockPositionValue"
import { usePromiseToCome } from "./utils/hooks/usePromiseToCome"
import { useCustomerOrderFullfilment } from "./utils/hooks/useCustomerOrderFullfilment"
import { useInventoryTurnover, filterByStatus, getTurnoverStatus } from "./utils/hooks/useInventoryTurnover"
import { Link } from "react-router-dom"
import { MdOutlineNavigateNext } from "react-icons/md";

ChartJS.register(ArcElement, Tooltip)

export default function Dashboard() {
	const month = moment().format("MM")
	const year = moment().format("YYYY")
	const pieLabelList: string[] = ["CURRENT", "LAST"]
	const labelList: string[] = Object.values(LabelListEnum);

	const [fill, setFill] = useState<DashboardFillType>({ month, year })
	const [filterPerformence, setFilterPerformence] = useState<string | undefined>(undefined)

	const date = `${fill?.year}${fill?.month}`
	const location_id = fill?.location_id

	const dataCategory = useProductCategory();
	const dataOverheadRatio = useOverheadRatio({ date, location_id })
	const dataSohToRevenue = useSohToRevenue({ date, location_id })
	const dataEmployeeCostToExpense = useEmployeeCostToExpense({ date, location_id }) 
	const dataEmployeeCostToRevenue = useEmployeeCostToRevenue({ date, location_id }) 

	const dataCurrentStockPerformence = useCurrentStockPerformence({
		date,
		location_id,
		category_id: filterPerformence
	})
	const dataPromiseToCome = usePromiseToCome({ location_id })
	const dataGMP = useGMP({ date, location_id })
	const dataStockAvailablityValue = useStockAvailablityValue({ date, location_id })

	const bar2List = dataStockAvailablityValue?.data?.map(item => item?.category_name)
	const stockValues = dataStockAvailablityValue?.data?.map(item => item?.stock_value)

	return (
		<section className="container my-5 flex flex-col gap-3">
			<HeaderMenu title="INVENTORY DASHBOARD" />

			<section className="flex flex-col gap-3">
				<section>
					<DashboardFilter
						onChangeFilter={(res) => setFill({
							...fill,
							month: moment(res.date_from).format("MM"),
							year: moment(res.date_from).format("YYYY"),
							location_id: res.location_id
						})}
					/>
				</section>
				<section className="grid lg:grid-cols-2 md:gird-cols-2 gap-3">
					<div><ValueStockSection fill={fill} /></div>
					<div><TotalUnits fill={fill} /></div>
				</section>

				<section className="flex w-full my-4">
					<div className="border p-3 w-full">
						<div className="text-center text-lg font-bold mb-3">Stock Availability Value</div>

						<MultipleChart
							className="min-h-[450px]"
							chartType="bar"
							data={{
								labels: bar2List,
								datasets: [{ data: stockValues, backgroundColor: ["blue"] }]
							}}
							options={{
								indexAxis: "y",
								scales: {
									x: {
										beginAtZero: true,
										min: Math.min(...stockValues) < 0 ? Math.min(...stockValues) : 0,
										max: Math.max(...stockValues),
										ticks: { maxRotation: 45, minRotation: 45 }
									},
								},
								plugins: {
									legend: { display: false },
									datalabels: { display: false },
								},
								maintainAspectRatio: false,
							}}
						/>
					</div>
				</section>

				<section className="grid xl:grid-cols-5 lg:grid-cols-3 md:gird-cols-2 gap-3">
					{labelList.map((item, key) => {
						let pieData = [0,0];
						// let backgroundColors = pieLabelList.map(() => uniqolor.random({ format: "rgb" }).color);
						let labels = pieLabelList;
						let displayValue = 0;

						if (item === LabelListEnum.OVERHEAD_RATIO && dataOverheadRatio?.data) {
							const { title, nilai_current, nilai_last } = dataOverheadRatio.data;

							pieData = [nilai_current ?? 0, nilai_last ?? 0];
							labels = pieLabelList;
							// backgroundColors = labels.map(() => uniqolor.random({ format: "rgb" }).color);
							displayValue = title ?? 0;
						}

						if (item === LabelListEnum.GMP && dataGMP?.data) {
							const { title, nilai_current, nilai_last } = dataGMP.data;

							pieData = [nilai_current ?? 0, nilai_last ?? 0];
							labels = pieLabelList;
							// backgroundColors = labels.map(() => uniqolor.random({ format: "rgb" }).color);
							displayValue = title ?? 0;
						}

						if (item === LabelListEnum.STO_TO_REVENUE_RATIO && dataSohToRevenue?.data) {
							const { title, nilai_current, nilai_last } = dataSohToRevenue.data;
							
							pieData = [nilai_current ?? 0, nilai_last ?? 0];
							labels = pieLabelList;
							// backgroundColors = labels.map(() => uniqolor.random({ format: "rgb" }).color);
							displayValue = title ?? 0;
						}

						if (item === LabelListEnum.EMPLOYEE_COST_RATIO && dataEmployeeCostToRevenue?.data) {
							const { title, nilai_current, nilai_last } = dataEmployeeCostToRevenue.data;

							pieData = [nilai_current ?? 0, nilai_last ?? 0];
							labels = pieLabelList;
							// backgroundColors = labels.map(() => uniqolor.random({ format: "rgb" }).color);
							displayValue = title ?? 0;
						}

						if (item === LabelListEnum.EMPLOYEE_COST_TO_EXPENSE_RATIO && dataEmployeeCostToExpense?.data) {
							const { title, nilai_current, nilai_last } = dataEmployeeCostToExpense.data;

							pieData = [nilai_current ?? 0, nilai_last ?? 0];
							labels = pieLabelList;
							// backgroundColors = labels.map(() => uniqolor.random({ format: "rgb" }).color);
							displayValue = title ?? 0;
						}

						

						const isEmptyPie = pieData.every(v => v === 0);

						return (
							<div key={key} className="border p-3 flex flex-col justify-between">
								<div className="text-center text-lg font-bold mb-3">{item}: {convertNumber(displayValue).intoCurrency}%</div>

								{isEmptyPie ? (
									<div className="flex items-center justify-center flex-1">
										<div className="w-full max-w-[550px] aspect-square rounded-full bg-gray-300 
										flex items-center justify-center">
											<span className="text-gray-500 text-sm">No Data</span>
										</div>
									</div>
								) : (
									<Pie
										className="max-h-[550px]"
										data={{ labels, datasets: [{ 
											data: pieData, 
											backgroundColor: [ "rgb(180, 83, 9)", "rgb(250, 204, 21)"]
										}] }}
										options={{
											plugins: {
												legend: { 
													position: "bottom",
													reverse: true
												},
												datalabels: {
													color: '#fff',
													font: { size: 20 },
													formatter: (value) => convertNumber(value).intoCurrency
												}
											}
										}}
									/>
								)}
							</div>
						)
					})}
				</section>

				<section className="grid xl:grid-cols-2 gap-[50px] mt-5 mb-[70px]">
					<section className="flex flex-col mt-5">
						<div className="flex items-center justify-between mb-2">
							<div className="text-center text-lg font-bold">CURRENT STOCK PERFORMANCE</div>

							<div>
								<Select
									className="w-[200px]"
									defaultValue={{ value: "0", label: "All" }}
									isLoading={dataCategory.isLoadingCategory}
									options={[{ label: "All", value: "" }].concat(dataCategory?.dataCategory)}
									value={dataCategory?.dataCategory.find((item) => item.value === filterPerformence)}
									onChange={(e) => setFilterPerformence(e?.value ?? "")}
								/>
							</div>
						</div>

						<section className="grow">
							<div className="max-h-[600px] overflow-y-auto">
								<BaseTable>
									<TableHeader>
										<tr>
											<th>CODE</th>
											<th>PRODUCT NAME</th>
											<th>SOH</th>
											<th>QTY ORDER</th>
											<th>STOCK IN</th>
											<th>STOCK OUT</th>
											<th>COMMITED</th>
										</tr>
									</TableHeader>
									<tbody>
										{dataCurrentStockPerformence?.data?.data?.map(performence => (
											<tr className="text-center">
												<td className="text-left">{performence?.product_barcode ?? "-"}</td>
												<td className="text-left">{performence?.product_name ?? "-"}</td>
												<td>{convertNumber(performence?.soh || 0)?.intoNormalAmount ?? "-"}</td>
												<td>{convertNumber(performence?.qty_order || 0)?.intoNormalAmount ?? "-"}</td>
												<td>{convertNumber(performence?.stock_in || 0)?.intoNormalAmount ?? "-"}</td>
												<td>{convertNumber(performence?.stock_out || 0)?.intoNormalAmount ?? "-"}</td>
												<td>{convertNumber(performence?.committed || 0)?.intoNormalAmount ?? "-"}</td>
											</tr>
										))}
									</tbody>
								</BaseTable>
							</div>
						</section>

						<div className="flex justify-end mt-2">
							<Link to="/inventory/report/dashboard/current-stock-performance">
								<Button color="primary">
									MORE
									<MdOutlineNavigateNext />
								</Button>
							</Link>
						</div>
					</section>

					<CurrentStockPositionValue
						dashboardFillter={fill}
					/>

					<section className="flex flex-col mt-5">
						<div className="flex items-center justify-between mt-3 mb-5">
							<div className="text-center text-lg font-bold">PURCHASE ORDER PROMISES TO COME</div>
						</div>

						<section className="grow">
							<div className="max-h-[600px] overflow-y-auto">
								<BaseTable>
									<TableHeader>
										<tr>
											<th>CODE</th>
											<th>VENDOR NAME</th>
											<th>PO ID</th>
											<th>ORDER DATE</th>
											<th>TOTAL QTY</th>
											<th>PROMISED DATE</th>
										</tr>
									</TableHeader>
									<tbody>
										{dataPromiseToCome?.data?.data?.map(performence => (
											<tr>
												<td className="text-left">{performence?.vendor_code ?? "-"}</td>
												<td className="text-left">{performence?.vendor_name ?? "-"}</td>
												<td>{performence?.trx_code ?? "-"}</td>
												<td>{performence?.transaction_date ? moment(performence.transaction_date).format("DD/MM/YYYY") : "-"}</td>
												<td className="text-center">{convertNumber(performence?.total_qty || 0)?.intoNormalAmount ?? "-"}</td>
												<td>{performence?.promize_date ? moment(performence.promize_date).format("DD/MM/YYYY") : "-"}</td>
											</tr>
										))}
									</tbody>
								</BaseTable>
							</div>
						</section>

						<div className="flex justify-end mt-2">
							<Link to="/inventory/report/dashboard/promise-to-come">
								<Button color="primary">
									MORE
									<MdOutlineNavigateNext />
								</Button>
							</Link>
						</div>
					</section>

					<CustomerOrderFullFilment
						dashboardFillter={fill}
					/>

					<InventoryTurnover
						dashboardFillter={fill}
					/>

				</section>
			</section>
		</section>
	)
}

export function CurrentStockPositionValue(props: {
	dashboardFillter: DashboardFillType
}) {
	const [fillterData, setFillterData] = useState<fillterDataType>({
		year_month: `${props.dashboardFillter?.year}${props.dashboardFillter?.month}`,
		location_id: props.dashboardFillter?.location_id ?? "",
		category_id: ""
	});

	const { dataCategory, isLoadingCategory } = useProductCategory();
	const { data, isLoading } = useCurrentStockPositionValue(fillterData)


	useEffect(() => {
		setFillterData(prev => ({
			...prev,
			year_month: `${props.dashboardFillter?.year}${props.dashboardFillter?.month}`,
			location_id: props.dashboardFillter?.location_id ?? ""
		}));
		//eslint-disable-next-line
	}, [
		props.dashboardFillter?.year,
		props.dashboardFillter?.month,
		props.dashboardFillter?.location_id
	]);

	return (
		<section className="flex flex-col  mt-5">
			<div className="flex items-center justify-between mb-2">
				<div className=" text-lg font-bold">CURRENT STOCK POSITION VALUE</div>
				<div>

					<Select className="w-[200px]"
						defaultValue={{ value: "0", label: "All" }}
						isLoading={isLoadingCategory}
						options={[{ label: "All", value: "" }].concat(dataCategory)}
						value={dataCategory.find((item) => item.value === fillterData.category_id)}
						onChange={(res) => {
							setFillterData(prev => ({
								...prev,
								category_id: res?.value as string
							}))
						}}
					/>
				</div>
			</div>

			<section className="grow">
				<div className="max-h-[600px] overflow-y-auto">
					<BaseTable>
						<TableHeader>
							<tr>
								<th>CODE</th>
								<th>PRODUCT NAME</th>
								<th>RETAIL VALUE</th>
								<th>COGM VALUE</th>
								<th>AVG VALUE</th>
								<th>GMP %</th>
								<th>PROFIT</th>
							</tr>
						</TableHeader>
						<tbody>
							{data.length === 0 ? (
								<tr>
									<td colSpan={6} className="text-center">
										<Loading loading={isLoading} errorText="No data available" />
									</td>
								</tr>
							) : (
								data.map(item => (
									<tr key={item.product_id}>
										<td className="text-left">{item?.product_barcode ?? "-"}</td>
										<td className="text-left">{item?.product_name ?? "-"}</td>
										<td className="text-right">{convertNumber(item?.retail_value || 0)?.intoCurrency ?? "-"}</td>
										<td className="text-right">{convertNumber(item?.cogm_value || 0)?.intoCurrency ?? "-"}</td>
										<td className="text-right">{convertNumber(item?.avg_price || 0)?.intoCurrency ?? "-"}</td>
										<td className="text-center">
											{item?.soh && item?.soh > 0 && item?.retail_value
												? `${convertNumber((item?.retail_value - item?.cogm_value) / item?.retail_value * 100).intoNormalAmount}%`
												: "0"}
										</td>
										<td className="text-right">
											{convertNumber(item?.retail_value - item?.cogm_value).intoCurrency}
										</td>
									</tr>
								))
							)}
						</tbody>
					</BaseTable>
				</div>
			</section>
			<div className="flex justify-end mt-2">
				<Link to="/inventory/report/dashboard/current-stock-position">
					<Button color="primary">
						MORE
						<MdOutlineNavigateNext />
					</Button>
				</Link>
			</div>
		</section>
	)
}

export function CustomerOrderFullFilment(props: {
	dashboardFillter: DashboardFillType
}) {
	const [fillterData, setFillterData] = useState<fillterDataType>({
		year_month: `${props.dashboardFillter?.year}${props.dashboardFillter?.month}`,
		location_id: props.dashboardFillter?.location_id ?? "",
		category_id: ""
	});

	// const { dataCategory, isLoadingCategory } = useProductCategory();
	const { data, isLoading } = useCustomerOrderFullfilment(fillterData)

	useEffect(() => {
		setFillterData(prev => ({
			...prev,
			year_month: `${props.dashboardFillter?.year}${props.dashboardFillter?.month}`,
			location_id: props.dashboardFillter?.location_id ?? ""
		}));
		//eslint-disable-next-line
	}, [
		props.dashboardFillter?.year,
		props.dashboardFillter?.month,
		props.dashboardFillter?.location_id
	]);

	return (
		<section className="flex flex-col  mt-5">
			<div className="flex items-center justify-between mb-5 mt-3">
				<div className=" text-lg font-bold">CUSTOMER ORDER FULLFILMENT</div>
			</div>

			<section className="grow">
				<div className="max-h-[600px] overflow-y-auto">
					<BaseTable>
						<TableHeader>
							<tr>
								<th>CODE</th>
								<th>CUSTOMER NAME</th>
								<th>ORDER ID</th>
								<th>SHIPMENT DATE</th>
								<th>TOTAL QTY</th>
								<th>PROMISED DATE</th>
							</tr>
						</TableHeader>
						<tbody>
							{data.length === 0 ? (
								<tr>
									<td colSpan={5} className="text-center">
										<Loading loading={isLoading} errorText="No data available" />
									</td>
								</tr>
							) : (
								data.map(item => (
									<tr key={item.customer_id}>
										<td className="text-left">{`${item?.customer_code ?? "-"}`}</td>
										<td className="text-left">{`${item?.customer_name ?? "-"}`}</td>
										<td className="text-left">{item.trx_code ?? "-"}</td>
										<td className="text-left">{moment(item.transaction_date).format('DD/MM/YYYY') ?? "-"}</td>
										<td className="text-right">{convertNumber(item.total_qty || 0)?.intoCurrency ?? "-"}</td>
										<td className="text-left">{moment(item.promize_date).format('DD/MM/YYYY') ?? "-"}</td>
									</tr>
								))
							)}
						</tbody>
					</BaseTable>
				</div>
			</section>
			<div className="flex justify-end mt-2">
				<Link to="/inventory/report/dashboard/customer-order-fullfilment">
					<Button color="primary">
						MORE
						<MdOutlineNavigateNext />
					</Button>
				</Link>
			</div>
		</section>
	)
}


export function InventoryTurnover(props: {
	dashboardFillter: DashboardFillType
}) {
	interface FillterType extends fillterDataType {
		status?: string
	}

	const [fillterData, setFillterData] = useState<FillterType>({
		year_month: `${props.dashboardFillter?.year}${props.dashboardFillter?.month}`,
		location_id: props.dashboardFillter?.location_id ?? "",
		category_id: "",
		status: ""
	});

	const { dataCategory, isLoadingCategory } = useProductCategory();
	const { data, isLoading, dropdownTurnover } = useInventoryTurnover(fillterData)
	const turnoverRules = data?.turnover_rule ?? [];

	useEffect(() => {
		setFillterData(prev => ({
			...prev,
			year_month: `${props.dashboardFillter?.year}${props.dashboardFillter?.month}`,
			location_id: props.dashboardFillter?.location_id ?? ""
		}));
		//eslint-disable-next-line
	}, [
		props.dashboardFillter?.year,
		props.dashboardFillter?.month,
		props.dashboardFillter?.location_id
	]);

	const filteredData = filterByStatus(
		data?.data ?? [],
		turnoverRules,
		fillterData.status
	);

	return (
		<section className="flex flex-col  mt-3">
			<div className="flex items-center justify-between mb-2">
				<div className=" text-lg font-bold">INVENTORY TURNOVER</div>
				<div className="flex gap-2">
					<Select className="w-[200px] "
						label="CATEGORY"
						defaultValue={{ value: "0", label: "All" }}
						isLoading={isLoadingCategory}
						options={[{ label: "All", value: "" }].concat(dataCategory)}
						value={dataCategory.find((item) => item.value === fillterData.category_id)}
						onChange={(res) => {
							setFillterData(prev => ({
								...prev,
								category_id: res?.value as string
							}))
						}}
					/>

					<Select className="w-[200px]"
						label="STATUS"
						defaultValue={{ value: "", label: "All" }}
						options={[{ label: "All", value: "" }].concat(dropdownTurnover)}
						onChange={(res) => {
							setFillterData(prev => ({
								...prev,
								status: res?.value as string
							}))
						}}
					/>
				</div>
			</div>

			<section className="grow">
				<div className="max-h-[600px] overflow-y-auto">
					<BaseTable>
						<TableHeader>
							<tr>
								<th>CODE</th>
								<th>PRODUCT NAME</th>
								<th>BEGIN QTY</th>
								<th>END QTY</th>
								<th>AVG QTY</th>
								<th>UNIT SOLD</th>
								<th className="w-[50px] text-center">
									<div className="flex flex-col items-center leading-tight">
										<span>TURNOVER</span>
										<span className="text-xs">(QTY)</span>
									</div>
								</th>
								<th className="w-[50px] text-center">
									<div className="flex flex-col items-center leading-tight">
										<span>DIO</span>
										<span className="text-xs">(Days)</span>
									</div>
								</th>
								<th>STATUS</th>
							</tr>
						</TableHeader>
						<tbody>

							{isLoading ? (
								<tr>
									<td colSpan={9} className="text-center">
										<Loading loading={isLoading} errorText="No data available" />
									</td>
								</tr>
							) : filteredData.length === 0 ? (
								<tr>
									<td colSpan={9} className="text-center">
										<div className="text-center">No data available</div>
									</td>
								</tr>
							) : (
								filteredData.map(item => {
									const rule = getTurnoverStatus(
										item.qty_turnover,
										turnoverRules
									);

									return (
										<tr key={item.product_id}>
											<td className="text-left">{item?.product_barcode ?? "-"}</td>
											<td className="text-left">{item?.product_name ?? "-"}</td>
											<td className="text-right">{convertNumber(item?.begin_qty || 0)?.intoCurrency ?? "-"}</td>
											<td className="text-right">{convertNumber(item?.end_qty || 0)?.intoCurrency ?? "-"}</td>
											<td className="text-right">{convertNumber(item?.avg_qty || 0)?.intoCurrency ?? "-"}</td>
											<td className="text-center">{convertNumber(item?.unit_sold || 0)?.intoCurrency ?? "-"}</td>
											<td className="text-right">
												{convertNumber(item?.qty_turnover || 0)?.intoCurrency ?? "-"}
											</td>
											<td className="text-right">
												{item?.qty_turnover && item?.qty_turnover > 0 && item?.avg_qty
													? convertNumber(365 / Number(item.qty_turnover)).intoNormalAmount
													: "0"}
											</td>
											<td>
												{rule?.mr_product_turnover_name ?? "-"}
											</td>
										</tr>
									)
								})
							)}
						</tbody>
					</BaseTable>
				</div>
			</section>

			<div className="flex justify-end mt-2">
				<Link to="/inventory/report/dashboard/inventory-turnover">
					<Button color="primary">
						MORE
						<MdOutlineNavigateNext />
					</Button>
				</Link>
			</div>
		</section>
	)
}